/**
 * `Notification`/`NotificationDelivery`/`NotificationPreference` read/write shapes — mirrors the
 * backend contract (backend/.../module/notify/api/dto/{NotificationView,DeliveryView,
 * NotificationPreferenceView,UnreadCountView,UpsertNotificationPreferenceRequest}.java).
 */

export const NOTIFICATION_SEVERITIES = ['info', 'warning', 'critical'] as const
export type NotificationSeverity = (typeof NOTIFICATION_SEVERITIES)[number]

/** `notification_delivery.status` (V20 CHECK) — queued -> sent -> delivered -> read/failed. */
export const DELIVERY_STATUSES = ['queued', 'sent', 'delivered', 'read', 'failed', 'suppressed'] as const
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number]

/** `notification_delivery.channel_kind` / `notification_preference.channel_kind` (V20 CHECK). */
export const CHANNEL_KINDS = ['in_app', 'email', 'teams', 'slack', 'push', 'sms', 'webhook'] as const
export type ChannelKind = (typeof CHANNEL_KINDS)[number]

export const CHANNEL_KIND_LABEL: Record<ChannelKind, string> = {
  in_app: 'In-app',
  email: 'Email',
  teams: 'Teams',
  slack: 'Slack',
  push: 'Push',
  sms: 'SMS',
  webhook: 'Webhook',
}

export const DIGEST_FREQUENCIES = ['none', 'daily', 'weekly'] as const
export type DigestFrequency = (typeof DIGEST_FREQUENCIES)[number]

export const DIGEST_FREQUENCY_LABEL: Record<DigestFrequency, string> = {
  none: 'Real-time',
  daily: 'Daily digest',
  weekly: 'Weekly digest',
}

/** `GET /api/v1/notifications` row — `deliveryStatus`/`deliveryId` are the CALLER's own in-app
 * delivery for this notification (`NotificationCenterService.list`'s own-principal join). */
export interface NotificationView {
  id: string
  subjectRef: string | null
  subjectType: string | null
  severity: NotificationSeverity | string
  category: string
  title: string
  renderedSummary: string | null
  createdAt: string
  deliveryStatus: DeliveryStatus | string
  deliveryId: string
}

export interface UnreadCountView {
  unread: number
}

/** `GET /api/v1/notifications/{id}/deliveries` (thin additive GET) — every channel this ONE
 * notification fanned out to for the CALLING recipient. */
export interface DeliveryView {
  id: string
  notificationId: string
  channelKind: ChannelKind | string
  recipientRef: string
  status: DeliveryStatus | string
  attempts: number
  error: string | null
  createdAt: string
}

export interface NotificationPreferenceView {
  id: string
  category: string
  channelKind: ChannelKind | string
  optIn: boolean
  digest: DigestFrequency | string
  quietHours: string | null
}

export interface UpsertNotificationPreferenceRequest {
  category: string
  channelKind: ChannelKind | string
  optIn: boolean
  digest?: DigestFrequency | string | null
  quietHours?: string | null
}
